import {
  AlertTriangle,
  Check,
  CreditCard,
  ExternalLink,
  Loader2,
  Swords,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { formatPrice, limitsToFeatureList } from "../../lib/plan-features";
import { trpc } from "../../lib/trpc";
import { Badge, Button } from "../ui";
import { ConfirmModal } from "./ConfirmModal";

type Props = {
  clubId: string;
  slug: string;
};

const BILLING_REFRESH_PARAM = "billing_refresh";

export function hasBillingRefreshSignal(search: URLSearchParams): boolean {
  return (
    search.has(BILLING_REFRESH_PARAM) ||
    search.has("customer_session_token") ||
    search.has("trial") ||
    search.has("upgraded")
  );
}

export function appendBillingRefresh(url: string): string {
  const nextUrl = new URL(url);
  nextUrl.searchParams.set(BILLING_REFRESH_PARAM, "1");
  return nextUrl.toString();
}

export function BillingSection({ clubId, slug }: Props) {
  const utils = trpc.useUtils();
  const { data: subscription, isLoading: subLoading } =
    trpc.subscription.getClubSubscription.useQuery({ clubId });
  const { data: plans, isLoading: plansLoading } = trpc.subscription.getPlans.useQuery();
  const { data: invoices } = trpc.subscription.getInvoices.useQuery({ clubId });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("yearly");
  const hasRefreshedReturnState = useRef(false);

  const refreshMutation = trpc.subscription.refreshClubSubscription.useMutation({
    onError: (err) => {
      toast.error(err.message || "Failed to refresh billing state");
    },
  });

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    if (!hasBillingRefreshSignal(search)) {
      return;
    }

    if (hasRefreshedReturnState.current) {
      return;
    }

    hasRefreshedReturnState.current = true;

    void (async () => {
      try {
        await refreshMutation.mutateAsync({ clubId });
        await Promise.all([
          utils.subscription.getClubSubscription.invalidate({ clubId }),
          utils.subscription.getInvoices.invalidate({ clubId }),
        ]);

        search.delete("customer_session_token");
        search.delete(BILLING_REFRESH_PARAM);
        search.delete("trial");
        search.delete("upgraded");

        const nextSearch = search.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
        window.history.replaceState({}, "", nextUrl);
      } catch {
        hasRefreshedReturnState.current = false;
      }
    })();
  }, [clubId, refreshMutation, utils]);

  const competitivePlan = plans?.find((p) => p.slug === "competitive");

  const checkoutMutation = trpc.subscription.createCheckoutSession.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => toast.error(err.message || "Failed to create checkout session"),
  });

  const portalMutation = trpc.subscription.createPortalSession.useMutation({
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => toast.error(err.message || "Failed to open billing portal"),
  });

  const cancelMutation = trpc.subscription.cancelSubscription.useMutation({
    onSuccess: () => {
      toast.success("Subscription will cancel at period end");
      void utils.subscription.getClubSubscription.invalidate({ clubId });
      setShowCancelModal(false);
    },
    onError: (err) => toast.error(err.message || "Failed to cancel subscription"),
  });

  if (subLoading || plansLoading) {
    return (
      <div className="space-y-6">
        <div className="h-32 rounded-3xl bg-surface-800/50 animate-pulse" />
        <div className="h-80 rounded-3xl bg-surface-800/50 animate-pulse" />
      </div>
    );
  }

  const plan = subscription?.plan;
  const status = subscription?.status;
  const isOnCasual = !subscription || plan?.tier === "casual";
  const isTrialing = status === "trialing";
  const isActive = status === "active";
  const isPastDue = status === "past_due";
  const isExpired = status === "expired";
  const hasTrialed = subscription?.hasTrialed ?? false;

  const trialEndsAt = subscription?.trialEndsAt ? new Date(subscription.trialEndsAt) : null;
  const now = new Date();
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  const periodEnd = subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;

  const statusBadge: Record<
    string,
    { label: string; color: "brand" | "green" | "yellow" | "red" | "gray" }
  > = {
    active: { label: "Active", color: "green" },
    trialing: { label: "Trial", color: "brand" },
    past_due: { label: "Past Due", color: "red" },
    canceled: { label: "Canceled", color: "gray" },
    expired: { label: "Expired", color: "yellow" },
  };

  const currentBadge = status ? statusBadge[status] : null;

  return (
    <div className="space-y-8">
      {/* Status Banners */}
      {isTrialing && trialDaysLeft <= 3 && trialDaysLeft > 0 && (
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl border border-yellow-500/30 bg-yellow-500/5">
          <AlertTriangle className="size-4 text-yellow-400 shrink-0" />
          <p className="text-sm text-yellow-300 flex-1">
            Your trial ends in{" "}
            <span className="font-bold">
              {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""}
            </span>
            . Upgrade to keep your features.
          </p>
        </div>
      )}

      {isExpired && (
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl border border-yellow-500/30 bg-yellow-500/5">
          <AlertTriangle className="size-4 text-yellow-400 shrink-0" />
          <p className="text-sm text-yellow-300 flex-1">
            Your previous billing session expired before activation. Start a new subscription to
            regain Competitive features.
          </p>
        </div>
      )}

      {isPastDue && (
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl border border-error-500/30 bg-error-500/5">
          <AlertTriangle className="size-4 text-error-400 shrink-0" />
          <p className="text-sm text-error-300 flex-1">
            Payment failed. Update your payment method to avoid losing access.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 !border-error-500/40 !text-error-400 hover:!bg-error-500/10"
            onClick={() =>
              portalMutation.mutate({
                clubId,
                returnUrl: appendBillingRefresh(window.location.href),
              })
            }
          >
            Update Payment
          </Button>
        </div>
      )}

      {/* Current Plan */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">
          Current Plan
        </h3>
        <div
          className={`relative overflow-hidden rounded-2xl border p-6 ${
            isOnCasual
              ? "bg-surface-950 border-surface-600"
              : "bg-surface-900/90 backdrop-blur-xl border-brand-500/50 shadow-[0_0_50px_-12px_color-mix(in_srgb,var(--color-brand-500)_25%,transparent)]"
          }`}
        >
          {!isOnCasual && (
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-40 bg-brand-500/8 rounded-full blur-[80px] pointer-events-none" />
          )}

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className={`p-3 rounded-xl border ${
                  isOnCasual
                    ? "bg-surface-500/20 border-surface-500/40"
                    : "bg-brand-500/20 border-brand-500/20"
                }`}
              >
                {isOnCasual ? (
                  <Users className="size-6 text-surface-300" />
                ) : (
                  <Swords className="size-6 text-brand-400" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h4 className="text-2xl font-bold text-white uppercase">
                    {plan?.name ?? "Casual"}
                  </h4>
                  {currentBadge && (
                    <Badge variant="subtle" color={currentBadge.color} size="sm">
                      {currentBadge.label}
                    </Badge>
                  )}
                  {subscription?.cancelAtPeriodEnd && (
                    <Badge variant="subtle" color="yellow" size="sm">
                      Canceling
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-surface-400 mt-1">
                  {isOnCasual && "Free forever — core features included"}
                  {isTrialing && trialEndsAt && `Trial ends ${trialEndsAt.toLocaleDateString()}`}
                  {isActive &&
                    !subscription?.cancelAtPeriodEnd &&
                    periodEnd &&
                    `Renews ${periodEnd.toLocaleDateString()}`}
                  {isActive &&
                    subscription?.cancelAtPeriodEnd &&
                    periodEnd &&
                    `Access until ${periodEnd.toLocaleDateString()}`}
                  {isPastDue && "Payment required to continue"}
                  {isExpired && "No active subscription"}
                </p>
              </div>
            </div>

            {!isOnCasual && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={
                  portalMutation.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <ExternalLink className="size-3.5" />
                  )
                }
                disabled={portalMutation.isPending}
                onClick={() =>
                  portalMutation.mutate({
                    clubId,
                    returnUrl: appendBillingRefresh(window.location.href),
                  })
                }
              >
                Manage Billing
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Upgrade / Trial */}
      {(isOnCasual || isExpired) && competitivePlan && (
        <section className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">
            {isOnCasual ? "Upgrade to Competitive" : "Upgrade"}
          </h3>
          <div className="relative group">
            <div className="absolute -inset-[1px] bg-gradient-to-b from-brand-400 to-transparent rounded-2xl opacity-30 blur-sm group-hover:opacity-60 transition duration-500" />

            <div className="relative overflow-hidden rounded-2xl bg-surface-900/90 backdrop-blur-xl border border-brand-500/50 p-8 shadow-[0_0_50px_-12px_color-mix(in_srgb,var(--color-brand-500)_25%,transparent)]">
              <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-48 bg-brand-500/6 rounded-full blur-[80px] pointer-events-none" />

              <div className="relative">
                {/* Billing Cycle Toggle */}
                <div className="flex justify-center mb-8">
                  <div className="relative inline-flex bg-surface-900/80 p-0.5 rounded-full border border-surface-700 backdrop-blur-md ring-1 ring-white/5">
                    <div
                      className={`absolute top-0.5 bottom-0.5 left-0.5 w-28 rounded-full bg-surface-700 shadow-inner border border-white/5 transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1.0)] ${
                        billingCycle === "yearly" ? "translate-x-28" : "translate-x-0"
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent rounded-full" />
                    </div>

                    <button
                      type="button"
                      onClick={() => setBillingCycle("monthly")}
                      className={`relative w-28 py-2 text-xs font-bold uppercase tracking-wider transition-colors duration-300 z-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 cursor-pointer ${
                        billingCycle === "monthly"
                          ? "text-white"
                          : "text-surface-400 hover:text-surface-200"
                      }`}
                    >
                      Monthly
                    </button>

                    <button
                      type="button"
                      onClick={() => setBillingCycle("yearly")}
                      className={`relative w-28 py-2 text-xs font-bold uppercase tracking-wider transition-colors duration-300 z-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 cursor-pointer flex items-center justify-center gap-1.5 ${
                        billingCycle === "yearly"
                          ? "text-white"
                          : "text-surface-400 hover:text-surface-200"
                      }`}
                    >
                      Yearly
                      <span
                        className={`px-1 py-0.5 text-2xs rounded font-black transition-all duration-300 ${
                          billingCycle === "yearly"
                            ? "bg-brand-500 text-surface-950 shadow-[0_0_10px_color-mix(in_srgb,var(--color-brand-500)_50%,transparent)]"
                            : "bg-brand-500/20 text-brand-400"
                        }`}
                      >
                        -25%
                      </span>
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="text-center mb-2">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-white">
                      {formatPrice(
                        billingCycle === "monthly"
                          ? competitivePlan.monthlyPrice
                          : Math.round(competitivePlan.yearlyPrice / 12),
                      )}
                    </span>
                    <span className="text-surface-400">/ mo</span>
                  </div>
                  <p className="text-sm mt-2 text-surface-400">
                    {billingCycle === "yearly" ? (
                      <span className="text-brand-400 font-bold">
                        Billed {formatPrice(competitivePlan.yearlyPrice)} yearly (Save{" "}
                        {formatPrice(
                          competitivePlan.monthlyPrice * 12 - competitivePlan.yearlyPrice,
                        )}
                        )
                      </span>
                    ) : (
                      "Billed monthly"
                    )}
                  </p>
                </div>

                <div className="h-px w-full bg-gradient-to-r from-transparent via-brand-500/50 to-transparent my-8" />

                {/* Feature list */}
                <ul className="grid grid-cols-2 gap-3 mb-8">
                  {limitsToFeatureList(competitivePlan.limits).map((feat) => (
                    <li key={feat} className="flex items-start gap-3 group/item">
                      <div className="flex-shrink-0 mt-0.5 p-0.5 rounded-full bg-brand-500/20 text-brand-400 group-hover/item:text-brand-300 transition-colors">
                        <Check className="size-3.5 stroke-[3]" />
                      </div>
                      <span className="text-sm text-white font-medium group-hover/item:text-surface-100 transition-colors">
                        {feat}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div className="space-y-3">
                  <Button
                    variant="primary"
                    className="w-full"
                    leftIcon={
                      checkoutMutation.isPending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Zap className="size-4" />
                      )
                    }
                    disabled={checkoutMutation.isPending}
                    onClick={() =>
                      checkoutMutation.mutate({
                        clubId,
                        planSlug: "competitive",
                        billingCycle,
                        successUrl: appendBillingRefresh(
                          `${window.location.origin}/app/clubs/${slug}/settings?section=billing`,
                        ),
                        cancelUrl: window.location.href,
                      })
                    }
                  >
                    {checkoutMutation.isPending
                      ? "Redirecting to checkout..."
                      : "Upgrade to Competitive"}
                  </Button>

                  {isOnCasual && !hasTrialed && (
                    <Button
                      variant="ghost"
                      className="w-full !text-brand-400 hover:!text-brand-300"
                      disabled={checkoutMutation.isPending}
                      onClick={() =>
                        checkoutMutation.mutate({
                          clubId,
                          planSlug: "competitive",
                          billingCycle,
                          withTrial: true,
                          successUrl: appendBillingRefresh(
                            `${window.location.origin}/app/clubs/${slug}/settings?section=billing`,
                          ),
                          cancelUrl: window.location.href,
                        })
                      }
                    >
                      Or start a {competitivePlan.trialDays}-day free trial
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Invoice History */}
      {invoices && invoices.length > 0 && (
        <section className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">
            Invoice History
          </h3>
          <div className="rounded-xl border border-surface-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-700 bg-surface-800/50">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-surface-400">
                    Date
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-surface-400">
                    Amount
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-surface-400">
                    Status
                  </th>
                  <th className="text-right px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={i} className="border-b border-surface-700 last:border-0">
                    <td className="px-4 py-3 text-surface-200">
                      {new Date(inv.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-white font-medium">{formatPrice(inv.amount)}</td>
                    <td className="px-4 py-3">
                      <Badge
                        color={
                          inv.status === "paid"
                            ? "green"
                            : inv.status === "open"
                              ? "yellow"
                              : "gray"
                        }
                      >
                        {inv.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {inv.url && (
                        <a
                          href={inv.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-surface-400 hover:text-white transition-colors"
                        >
                          View <ExternalLink className="size-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Cancel Subscription */}
      {(isActive || isTrialing) && !subscription?.cancelAtPeriodEnd && (
        <section className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-error-400">
            Cancel Subscription
          </h3>
          <div className="p-5 rounded-xl border border-error-500/30 bg-error-500/5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">Cancel your plan</p>
                <p className="text-xs text-surface-400 mt-0.5">
                  {isTrialing
                    ? "You'll keep access until your trial ends, then be downgraded to Casual."
                    : "You'll keep access until the end of your billing period."}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="!border-error-500/40 !text-error-400 hover:!bg-error-500/10"
                onClick={() => setShowCancelModal(true)}
              >
                Cancel Plan
              </Button>
            </div>
          </div>

          <ConfirmModal
            open={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            title="Cancel Subscription"
            description={
              isTrialing
                ? "Are you sure? You'll keep Competitive features until your trial ends, then be downgraded to Casual."
                : "Are you sure? You'll keep access until the end of your current billing period, then be downgraded to Casual."
            }
            confirmLabel="Cancel Plan"
            isPending={cancelMutation.isPending}
            variant="danger"
            icon={<CreditCard className="size-5 text-error-400" />}
            onConfirm={() => cancelMutation.mutate({ clubId })}
          />
        </section>
      )}
    </div>
  );
}
